import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, List, Columns3, CalendarDays, GanttChart, Sparkles } from "lucide-react";
import { Kpi, Sel, PageHead } from "@/components/tm/TmParts";
import { TmList, TmKanban, TmCalendar, TmTimeline, BulkBar } from "@/components/tm/TmViews";
import { OfficeTaskForm } from "@/components/office/OfficeTaskForm";
import { OfficeTaskDetail } from "@/components/office/OfficeTaskDetail";

const TITLES = { all: ["All Tasks", "Every task in your permitted scope — one engine, every view."], mine: ["My Tasks", "Assigned to you or where you collaborate."], team: ["Team Tasks", "Your reporting team's work, blockers and reviews."] };
const VIEWS = [["list", "List", List], ["kanban", "Kanban", Columns3], ["calendar", "Calendar", CalendarDays], ["timeline", "Timeline", GanttChart]];
const MGMT = ["admin", "director", "operations_head", "warehouse_manager", "supervisor", "sales_head", "factory_head", "finance_head"];

export default function TaskManagement({ scope = "all" }) {
  const { user } = useAuth();
  const [sp, setSp] = useSearchParams();
  const view = sp.get("view") || "list";
  const openTask = sp.get("task");
  const [meta, setMeta] = useState(null);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [f, setF] = useState({ workspace: sp.get("workspace") || localStorage.getItem("tm_workspace") || "", department: sp.get("department") || "", status: sp.get("status") || "", priority: sp.get("priority") || "", assignee: sp.get("assignee") || "", project_id: "", q: "", overdue: sp.get("overdue") === "1" });
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [cols, setCols] = useState(null);
  const [cal, setCal] = useState(null);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [tl, setTl] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [aiText, setAiText] = useState("");
  const [aiDraft, setAiDraft] = useState(null);
  const [selected, setSelected] = useState([]);
  const canBulk = MGMT.includes(user?.role);

  useEffect(() => {
    api.get("/office/meta").then((r) => setMeta(r.data)).catch(() => {});
    api.get("/office/directory").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/office/projects").then((r) => setProjects(r.data)).catch(() => {});
  }, []);

  const params = useCallback(() => {
    const p = { view: scope, page, page_size: 50 };
    Object.entries(f).forEach(([k, v]) => { if (v) p[k] = v === true ? true : v; });
    return p;
  }, [f, scope, page]);

  const reload = useCallback(async () => {
    const p = params();
    if (view === "list") setData((await api.get("/tm/tasks", { params: p })).data);
    else if (view === "kanban") setCols((await api.get("/tm/kanban", { params: p })).data);
    else if (view === "calendar") {
      const [y, m] = month.split("-").map(Number);
      const end = `${month}-${String(new Date(y, m, 0).getDate()).padStart(2, "0")}`;
      setCal((await api.get("/tm/calendar", { params: { start: `${month}-01`, end, view: scope, workspace: f.workspace || undefined } })).data);
    } else if (view === "timeline") setTl((await api.get("/tm/timeline", { params: { view: scope, workspace: f.workspace || undefined, project_id: f.project_id || undefined } })).data);
    if (view !== "list") setData((await api.get("/tm/tasks", { params: { ...p, page_size: 1 } })).data);
  }, [params, view, month, scope, f.workspace, f.project_id]);
  useEffect(() => { reload().catch(() => {}); }, [reload]);

  const set = (k, v) => { setF((p) => ({ ...p, [k]: v })); setPage(1); if (k === "workspace") localStorage.setItem("tm_workspace", v); };
  const setView = (v) => { sp.set("view", v); setSp(sp, { replace: true }); };
  const open = (id) => { sp.set("task", id); setSp(sp, { replace: true }); };
  const close = () => { sp.delete("task"); setSp(sp, { replace: true }); };
  const draftAi = async () => {
    if (!aiText.trim()) return;
    const r = await api.post("/tm/ai/draft-task", { text: aiText });
    if (r.data.ok && r.data.draft) { setAiDraft(r.data.draft); setFormOpen(true); } else window.alert(r.data.text);
  };

  const [title, sub] = TITLES[scope];
  const s = data?.summary;
  const depts = meta ? (f.workspace ? meta.workspaces[f.workspace] : meta.departments) : [];
  const base = scope === "mine" ? "/tm/my" : scope === "team" ? "/tm/team" : "/tm";
  return (
    <div className="space-y-5" data-testid={`tm-page-${scope}`}>
      <PageHead title={title} sub={sub} right={<>
        <div className="flex rounded-md border border-border bg-card overflow-hidden" data-testid="tm-view-switch">
          {VIEWS.map(([k, l, I]) => <button key={k} onClick={() => setView(k)} className={`flex items-center gap-1.5 px-3 h-9 text-xs font-medium ${view === k ? "bg-primary text-white" : "hover:bg-accent"}`} data-testid={`tm-view-${k}`}><I className="w-3.5 h-3.5" />{l}</button>)}
        </div>
        <Button className="bg-primary text-white" onClick={() => { setAiDraft(null); setFormOpen(true); }} data-testid="tm-new-task"><Plus className="w-4 h-4 mr-1" />New Task</Button>
      </>} />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Kpi label="Total" value={s?.total} testid="kpi-total" />
        <Kpi label="Due Today" value={s?.due_today} tone="text-primary" to={`${base}?due_from=${new Date().toISOString().slice(0, 10)}&due_to=${new Date().toISOString().slice(0, 10)}`} testid="kpi-today" />
        <Kpi label="Overdue" value={s?.overdue} tone={s?.overdue ? "text-signal-red" : ""} to={`${base}?overdue=1`} testid="kpi-overdue" />
        <Kpi label="Blocked" value={s?.blocked} tone={s?.blocked ? "text-amber-600" : ""} to={`${base}?status=blocked`} testid="kpi-blocked" />
        <Kpi label="In Review" value={s?.review} tone="text-purple-600" to={`${base}?status=completed`} testid="kpi-review" />
        <Kpi label="Completed" value={s?.completed} tone="text-emerald-600" to={`${base}?status=verified,closed`} testid="kpi-completed" />
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="tm-filters">
        <Input placeholder="Search tasks, IDs, people…" className="h-9 w-56 bg-card text-xs" value={f.q} onChange={(e) => set("q", e.target.value)} data-testid="tm-search" />
        <Sel value={f.workspace} onChange={(v) => { set("workspace", v); set("department", ""); }} placeholder="All Workspaces" options={[{ value: "office", label: "Office Work" }, { value: "factory", label: "Factory & Warehouse" }]} testid="tm-f-workspace" />
        <Sel value={f.department} onChange={(v) => set("department", v)} placeholder="All Departments" options={depts} testid="tm-f-department" />
        <Sel value={f.status} onChange={(v) => set("status", v)} placeholder="All Status" options={(meta?.statuses || []).map((x) => ({ value: x.key, label: x.label }))} testid="tm-f-status" />
        <Sel value={f.priority} onChange={(v) => set("priority", v)} placeholder="All Priority" options={meta?.priorities || []} testid="tm-f-priority" />
        {scope !== "mine" && <Sel value={f.assignee} onChange={(v) => set("assignee", v)} placeholder="All Owners" options={users.map((u) => ({ value: u.user_id, label: u.name }))} testid="tm-f-assignee" />}
        <Sel value={f.project_id} onChange={(v) => set("project_id", v)} placeholder="All Projects" options={projects.map((p) => ({ value: p.project_id, label: p.name }))} testid="tm-f-project" />
        <label className="flex items-center gap-1.5 text-xs"><input type="checkbox" className="accent-primary" checked={f.overdue} onChange={(e) => set("overdue", e.target.checked)} data-testid="tm-f-overdue" />Overdue only</label>
        {(f.q || f.workspace || f.department || f.status || f.priority || f.assignee || f.project_id || f.overdue) && <Button size="sm" variant="ghost" onClick={() => setF({ workspace: "", department: "", status: "", priority: "", assignee: "", project_id: "", q: "", overdue: false })} data-testid="tm-clear">Clear</Button>}
      </div>

      <div className="flex gap-2 items-center" data-testid="tm-ai-bar">
        <Sparkles className="w-4 h-4 text-primary shrink-0" />
        <Input placeholder="AI: describe a request → draft a task (e.g. 'Ask Purchase to get 3 quotes for 28mm caps by Friday')" className="h-9 bg-card text-xs" value={aiText} onChange={(e) => setAiText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && draftAi()} data-testid="tm-ai-input" />
        <Button size="sm" variant="outline" onClick={draftAi} data-testid="tm-ai-draft">Draft</Button>
      </div>

      {canBulk && view === "list" && <BulkBar selected={selected} clear={() => setSelected([])} users={users} reload={reload} />}
      {view === "list" && <TmList data={data} onOpen={open} selected={selected} setSelected={setSelected} canBulk={canBulk} page={page} setPage={setPage} />}
      {view === "kanban" && <TmKanban cols={cols} onOpen={open} reload={reload} />}
      {view === "calendar" && <TmCalendar data={cal} month={month} setMonth={setMonth} onOpen={open} />}
      {view === "timeline" && <TmTimeline rows={tl} onOpen={open} />}

      {meta && <OfficeTaskForm open={formOpen} onOpenChange={setFormOpen} users={users} projects={projects} meta={meta} onSaved={reload} prefill={aiDraft} />}
      {openTask && <OfficeTaskDetail taskId={openTask} onClose={close} onChanged={reload} />}
    </div>);
}
